/**
 * HTML Card bridge types + runtime
 *
 * Muse-style state bridge for sandboxed chat HTML fragments:
 * - getState(defaults) seeds once, then returns current state
 * - setState(next) always replaces the whole object
 * - state event is the single source of truth; render is pure from state
 * - text/sliders persist on change/blur (not every keystroke/tick)
 */

export const HTML_CARD_MAX_BYTES = 1_000_000
export const HTML_CARD_FALLBACK_MAX_CHARS = 120
export const HTML_CARD_MESSAGE_SOURCE = 'losi-html-card' as const
export const HTML_CARD_HOST_SOURCE = 'losi-html-card-host' as const

export type HtmlCardState = Record<string, unknown>

export type HtmlCardToHostMessage =
  | { source: typeof HTML_CARD_MESSAGE_SOURCE; type: 'ready'; cardId: string }
  | {
      source: typeof HTML_CARD_MESSAGE_SOURCE
      type: 'setState'
      cardId: string
      state: HtmlCardState
    }
  | {
      source: typeof HTML_CARD_MESSAGE_SOURCE
      type: 'submit'
      cardId: string
      payload: string
    }
  | {
      source: typeof HTML_CARD_MESSAGE_SOURCE
      type: 'resize'
      cardId: string
      height: number
    }
  | {
      source: typeof HTML_CARD_MESSAGE_SOURCE
      type: 'error'
      cardId: string
      message: string
    }

export type HtmlCardFromHostMessage =
  | {
      source: typeof HTML_CARD_HOST_SOURCE
      type: 'init'
      cardId: string
      state: HtmlCardState
    }
  | {
      source: typeof HTML_CARD_HOST_SOURCE
      type: 'state'
      cardId: string
      state: HtmlCardState
    }
  | {
      source: typeof HTML_CARD_HOST_SOURCE
      type: 'theme'
      cardId: string
      /** Full replacement CSS for the injected #losi-card-theme style. */
      css: string
    }

export function isHtmlCardToHostMessage(value: unknown): value is HtmlCardToHostMessage {
  if (!value || typeof value !== 'object') return false
  const message = value as Record<string, unknown>
  return (
    message.source === HTML_CARD_MESSAGE_SOURCE &&
    typeof message.type === 'string' &&
    typeof message.cardId === 'string'
  )
}

/** Byte size of a UTF-8 string (approx via TextEncoder). */
export function measureHtmlCardBytes(html: string): number {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(html).byteLength
  }
  return html.length
}

export function clampHtmlCardFallback(fallback: string | undefined | null): string {
  const trimmed = (fallback || '').trim().replace(/\s+/g, ' ')
  if (!trimmed) return 'Interactive card'
  return trimmed.length > HTML_CARD_FALLBACK_MAX_CHARS
    ? `${trimmed.slice(0, HTML_CARD_FALLBACK_MAX_CHARS - 1)}…`
    : trimmed
}

/**
 * Injected into every HTML card iframe.
 * Keeps state only via the host bridge — no localStorage / sessionStorage.
 */
export function buildHtmlCardBridgeRuntime(cardId: string): string {
  // Keep this as a string — it runs inside the iframe, not the host bundle.
  return `
(function () {
  var CARD_ID = ${JSON.stringify(cardId)};
  var SOURCE = ${JSON.stringify(HTML_CARD_MESSAGE_SOURCE)};
  var HOST = ${JSON.stringify(HTML_CARD_HOST_SOURCE)};
  var state = {};
  var seeded = false;
  var listeners = [];

  function post(msg) {
    msg.source = SOURCE;
    msg.cardId = CARD_ID;
    parent.postMessage(msg, '*');
  }

  function emitState() {
    for (var i = 0; i < listeners.length; i++) {
      try { listeners[i](state); } catch (e) {}
    }
  }

  function getState(defaults) {
    if (!seeded) {
      seeded = true;
      if (defaults && typeof defaults === 'object') {
        state = Object.assign({}, defaults);
      } else {
        state = {};
      }
      post({ type: 'setState', state: state });
    }
    return state;
  }

  function setState(next) {
    if (!next || typeof next !== 'object') next = {};
    state = next;
    seeded = true;
    post({ type: 'setState', state: state });
    emitState();
  }

  function onState(cb) {
    if (typeof cb === 'function') listeners.push(cb);
    return function unsubscribe() {
      listeners = listeners.filter(function (fn) { return fn !== cb; });
    };
  }

  function submit(payload) {
    post({ type: 'submit', payload: String(payload || '') });
  }

  function reportHeight() {
    var height = Math.max(
      document.documentElement.scrollHeight,
      document.body ? document.body.scrollHeight : 0,
      80
    );
    post({ type: 'resize', height: height });
  }

  window.LosiCard = {
    getState: getState,
    setState: setState,
    onState: onState,
    submit: submit
  };

  // Report card script failures (parse or runtime, before the card is
  // interactive) so the host can show a fallback instead of a silently
  // half-painted card.
  window.addEventListener('error', function (e) {
    post({ type: 'error', message: String((e && e.message) || 'Card script error') });
  }, true);
  window.addEventListener('unhandledrejection', function (e) {
    var reason = e && e.reason;
    post({ type: 'error', message: String((reason && reason.message) || reason || 'Card script error') });
  });

  window.addEventListener('message', function (event) {
    var data = event.data;
    if (!data || data.source !== HOST || data.cardId !== CARD_ID) return;
    if (data.type === 'init' || data.type === 'state') {
      if (data.state && typeof data.state === 'object') {
        state = data.state;
        seeded = true;
        emitState();
      }
    }
    // Theme CSS vars are baked to absolute values at srcDoc build time,
    // so a host dark/light flip must push fresh CSS or the card keeps
    // rendering in the theme it was born with.
    if (data.type === 'theme' && typeof data.css === 'string') {
      var themeEl = document.getElementById('losi-card-theme');
      if (themeEl) themeEl.textContent = data.css;
    }
  });

  window.addEventListener('load', function () {
    post({ type: 'ready' });
    reportHeight();
  });

  if (typeof ResizeObserver !== 'undefined') {
    var ro = new ResizeObserver(function () { reportHeight(); });
    ro.observe(document.documentElement);
  }

  // Prevent accidental navigation / form posts inside the card.
  document.addEventListener('submit', function (e) {
    e.preventDefault();
  }, true);
})();
`.trim()
}
